import { useState } from "react";
import { useParams } from "react-router-dom";
import { Table, Button, Modal, Form, Input, message, Select, DatePicker, Space, Popconfirm } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useEpics, useCreateEpic, useUpdateEpic, useDeleteEpic } from "../../queries/epics";
import { useProject } from "../../queries/projects";
import { useTasks } from "../../queries/tasks";
import { EpicProps, EpicStatus, TaskProps } from "../../types/interfaces";
import { ApiError } from "../../hooks/apiRequest";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { ProjectNav } from "../../components/ui";
import { PageShell, Toolbar } from "../../components/ui/PageShell";
import { EpicStatusTag } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";

export const EpicListPage = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const { data: epics, isLoading, isError, error } = useEpics(projectId!);
  const { data: project } = useProject(projectId!);
  const { data: tasks } = useTasks({ projectId: projectId! });
  const createEpic = useCreateEpic(projectId!);
  const updateEpic = useUpdateEpic(projectId!);
  const deleteEpic = useDeleteEpic(projectId!);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEpic, setEditingEpic] = useState<EpicProps | null>(null);
  const [form] = Form.useForm();

  const handleSave = async () => {
    const values = await form.validateFields();
    try {
      const data = {
        ...values,
        targetDate: values.targetDate?.format("YYYY-MM-DD"),
      };
      if (editingEpic) {
        await updateEpic.mutateAsync({ id: editingEpic._id, data });
      } else {
        await createEpic.mutateAsync(data);
      }
      setIsModalOpen(false);
      setEditingEpic(null);
      form.resetFields();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not save the epic");
    }
  };

  const openCreate = () => {
    setEditingEpic(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEdit = (epic: EpicProps) => {
    setEditingEpic(epic);
    form.setFieldsValue({
      title: epic.title,
      description: epic.description,
      status: epic.status,
      targetDate: epic.targetDate ? dayjs(epic.targetDate) : undefined,
    });
    setIsModalOpen(true);
  };

  const open = (epics || []).filter((e: EpicProps) => e.status !== "done").length;

  return (
    <PageShell
      kind="list"
      crumbs={[{ label: "Projects", to: "/projects" }, { label: project?.name || "Project" }]}
      title="Epics"
      subtitle="The large threads of work this project breaks down into."
      meta={[
        { label: "Total", value: <span className="osi-num">{(epics || []).length}</span> },
        { label: "Open", value: <span className="osi-num">{open}</span> },
      ]}
      actions={<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New epic</Button>}
      tabs={<ProjectNav projectId={projectId!} active="epics" />}
    >
      <Toolbar count={(epics || []).length} unit="epics" />
      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        <Table<EpicProps>
          rowKey="_id"
          size="small"
          dataSource={epics || []}
          pagination={false}
          locale={{
            emptyText: (
              <EmptyState
                title="No epics yet"
                description="Epics group related tasks so the board and the backlog stay readable once the project grows."
                actions={<Button type="primary" onClick={openCreate}>New epic</Button>}
              />
            ),
          }}
          columns={[
            { title: "Title", dataIndex: "title", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
            {
              title: "Description", dataIndex: "description",
              render: (v?: string) => v
                ? <span style={{ color: "var(--osi-text-muted)" }}>{v}</span>
                : <span style={{ color: "var(--osi-text-faint)" }}>—</span>,
            },
            { title: "Status", dataIndex: "status", width: 140, render: (s: EpicStatus) => <EpicStatusTag status={s} /> },
            {
              title: "Tasks", width: 110,
              render: (_, epic) => {
                const epicTasks = (tasks || []).filter((task: TaskProps) => task.epicId === epic._id);
                const done = epicTasks.filter((task: TaskProps) => task.status === "done").length;
                return <span className="osi-num">{done}/{epicTasks.length}</span>;
              },
            },
            {
              title: "Target", dataIndex: "targetDate", width: 130, align: "right",
              render: (v?: string) => v
                ? <span className="osi-num">{dayjs(v).format("D MMM YYYY")}</span>
                : <span style={{ color: "var(--osi-text-faint)" }}>—</span>,
            },
            {
              title: "", width: 180, align: "right",
              render: (_, epic) => (
                <Space size="small">
                  <Button size="small" aria-label={`Edit ${epic.title}`} onClick={() => openEdit(epic)}>Edit</Button>
                  <Popconfirm
                    title="Delete this epic?"
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => deleteEpic.mutate(epic._id, {
                      onError: (err) => message.error(err instanceof ApiError ? err.message : "Could not delete the epic"),
                    })}
                  >
                    <Button danger size="small" aria-label={`Delete ${epic.title}`}>Delete</Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </DisplayComponent>

      <Modal
        title={editingEpic ? "Edit epic" : "New epic"}
        open={isModalOpen}
        onOk={handleSave}
        okText={editingEpic ? "Save changes" : "Create epic"}
        onCancel={() => { setIsModalOpen(false); setEditingEpic(null); }}
        confirmLoading={createEpic.isPending || updateEpic.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="Title" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={3} /></Form.Item>
          {editingEpic && (
            <Form.Item name="status" label="Status">
              <Select options={[
                { label: "Open", value: "open" },
                { label: "In progress", value: "in_progress" },
                { label: "Done", value: "done" },
              ]} />
            </Form.Item>
          )}
          <Form.Item name="targetDate" label="Target date"><DatePicker style={{ width: "100%" }} /></Form.Item>
        </Form>
      </Modal>
    </PageShell>
  );
};
